import {
	AUTHENTICATION_PROVIDER,
	AUTHENTICATION_URL_PARAMS,
	AuthType,
	forgeRefreshTokenCookie,
	HeaderMenuOption,
} from '../../../../../types';
import AboutBox, { AboutBoxProps } from '../../../../../components/AboutBox';
import {
	AvatarWrapper,
	ButtonChild,
	InlineDiv,
	MenuElement,
	StyledMenu,
} from './UserProfile.styles';
import { MenuItem, Link as MuiLink } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	useAdpAnalytics,
	useGlobalization,
	useSiteConfig,
} from '../../../../../contexts';

import { AboutIcon } from '../icons/AboutIcon';
import ArrowIcon from '../icons/ArrowIcon';
import AutodeskPortalIcon from '../icons/AutodeskPortalIcon';
import FeedbackLinesIcon from '../icons/FeedbackLinesIcon';
import LogoutIcon from '../icons/LogoutIcon';
import RoundButton from '../../../../../components/RoundButton/RoundButton';
import cookies from 'browser-cookies';
import { getAuthenticationProvider } from '../../../../../utils/getAuthenticationProvider';
import { selectAuthenticationContext } from '../../../../../selectors';
import useLogoutRoute from '../../../../../hooks/useLogoutRoute';
import { userEndEmulation } from '../../../../../actions';
import { UserEmulationIcon } from '../../../../../icons/UserEmulation';
import { EndUserEmulationIcon } from '../../../../../icons/EndUserEmulation';
import ThemePicker from '../../../theme/ThemePicker';
import { useWeaveThemeContext } from '../../../theme/WeaveContext';
import { legacyInnovyzeColors } from '../../../theme/themes/specificStyles/pallette.inno';
import { useCache } from '../../../../../hooks/useCache';
import Avatar, { sizes as avatarSizes } from '@weave-design/avatar';

interface UserProfileProps {
	aboutBox?: Omit<AboutBoxProps, 'open' | 'setOpen'>;
	hasLoginPicker?: boolean;
	inEmulation?: boolean;
	customProfileOptions?: HeaderMenuOption[];
}

export const UserProfile = ({
	aboutBox,
	hasLoginPicker,
	inEmulation,
	customProfileOptions,
}: UserProfileProps): JSX.Element => {
	const { isWeaveTheme, weaveThemeConsumer } = useWeaveThemeContext();
	const resolvedRoles = weaveThemeConsumer?.resolvedRoles;
	const { config } = useSiteConfig();
	const isForgeFlow = getAuthenticationProvider() === AuthType.Forge;
	const { logout, user, client } = useSelector(selectAuthenticationContext);
	const { setLogoutRoute } = useLogoutRoute();
	const { t } = useGlobalization();
	const adp = useAdpAnalytics();
	const { clearCache } = useCache();
	const getLogoutRouteParams = (
		switchAccount: boolean,
	): AUTHENTICATION_URL_PARAMS => {
		const authType = localStorage.getItem(
			AUTHENTICATION_PROVIDER,
		) as AuthType | null;
		const hasForgeRefreshToken = cookies.get(forgeRefreshTokenCookie);
		if (switchAccount) return AUTHENTICATION_URL_PARAMS.PICKER;

		return authType === AuthType.Forge || hasForgeRefreshToken
			? AUTHENTICATION_URL_PARAMS.AUTODESK
			: AUTHENTICATION_URL_PARAMS.AUTH0;
	};

	const logoutAction = ({ switchAccount }: { switchAccount: boolean }) => {
		const logoutRouteParams = getLogoutRouteParams(switchAccount);
		setLogoutRoute(logoutRouteParams);

		if (logout) {
			clearCache();
			setTimeout(() => {
				logout(undefined, client);
			}, 50);
		}
	};

	const userName = user?.email ?? '';
	const [anchorEl, setAnchorEl] =
		useState<HTMLButtonElement | HTMLDivElement | null>(null);

	const [aboutBoxOpen, setAboutBoxOpen] = useState<boolean>(false);

	const closeMenu = () => setAnchorEl(null);
	const dispatch = useDispatch();

	const textColor =
		isWeaveTheme && resolvedRoles['colorScheme.text.default']
			? resolvedRoles['colorScheme.text.default']
			: 'white';

	const backgroundColor =
		isWeaveTheme && resolvedRoles['menu.backgroundColor']
			? resolvedRoles['menu.backgroundColor']
			: legacyInnovyzeColors.sideBarGreen;

	const profileMenuOptions = useMemo(() => {
		if (customProfileOptions?.length) {
			return customProfileOptions;
		}
		const options: HeaderMenuOption[] = [];
		if (inEmulation)
			options.push({
				content: (
					<MuiLink color="inherit" underline={'none'}>
						<MenuElement
							onClick={e => {
								e.preventDefault();
								dispatch(userEndEmulation());
								closeMenu();
							}}>
							<EndUserEmulationIcon />
							<span>{t('End User Emulation')}</span>
						</MenuElement>
					</MuiLink>
				),
			});
		if (aboutBox) {
			options.push({
				title: aboutBox.title,
				content: (
					<MenuElement>
						<AboutIcon color={textColor} />{' '}
						<span>{aboutBox.title}</span>
					</MenuElement>
				),
				onClick: () => setAboutBoxOpen(!aboutBoxOpen),
			});
		}
		if (isForgeFlow) {
			options.push({
				title: t('Click to manage Autodesk Account'),
				content: (
					<MuiLink
						href={
							config?.forge?.portalDomain
								? config?.forge?.portalDomain
								: 'https://manage.autodesk.com/home'
						}
						target="_blank"
						rel="noopener noreferrer"
						color="inherit"
						underline={'none'}>
						<MenuElement>
							<AutodeskPortalIcon color={textColor} />{' '}
							<span>
								{t('Autodesk Account')} <ArrowIcon />
							</span>
						</MenuElement>
					</MuiLink>
				),
			});
		}
		options.push({
			title: t('Click to provide feedback'),
			content: (
				<MuiLink
					href="https://feedback.innovyze.com/forums/924217-info360-and-operational-analytics"
					target="_blank"
					rel="noopener noreferrer"
					color="inherit"
					underline={'none'}>
					<MenuElement>
						<FeedbackLinesIcon color={textColor} />{' '}
						<span>{t('Provide Feedback')}</span>
					</MenuElement>
				</MuiLink>
			),
		});
		return options;
	}, [
		aboutBox,
		inEmulation,
		isForgeFlow,
		hasLoginPicker,
		customProfileOptions,
		textColor,
	]);

	return (
		<InlineDiv>
			{inEmulation ? (
				<RoundButton onClick={e => setAnchorEl(e.currentTarget)}>
					<UserEmulationIcon />
				</RoundButton>
			) : (
				<AvatarWrapper onClick={e => setAnchorEl(e.currentTarget)}>
					<Avatar
						size={avatarSizes.MEDIUM_32}
						firstName={user?.firstName}
						lastName={user?.lastName}
					/>
				</AvatarWrapper>
			)}
			<StyledMenu
				id={'profile-menu'}
				anchorEl={anchorEl}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				$backgroundColor={backgroundColor}
				open={!!anchorEl}
				onClose={closeMenu}
				$textColor={textColor}>
				{profileMenuOptions.map((option, index) => {
					return (
						<MenuItem
							key={`${index}-${option.title}`}
							title={option?.title}
							onClick={() => option.onClick?.()}>
							{option.content}
						</MenuItem>
					);
				})}
				<MenuItem
					title={t('Click to sign out {{userName}}', { userName })}>
					<MuiLink color="inherit" underline={'none'}>
						<MenuElement
							onClick={e => {
								e.preventDefault();
								adp?.track('SESSION_END');
								logoutAction({ switchAccount: false });
								closeMenu();
							}}>
							<LogoutIcon color={textColor} />{' '}
							<span>{t('Log Out')}</span>
						</MenuElement>
						{hasLoginPicker && (
							<MenuElement
								onClick={e => {
									e.preventDefault();
									adp?.track('SESSION_END');
									logoutAction({ switchAccount: true });
									closeMenu();
								}}>
								<div />
								<ButtonChild
									type="button"
									title={t('Click to switch your account')}>
									{t('Sign in with a different domain')}
								</ButtonChild>
							</MenuElement>
						)}
					</MuiLink>
				</MenuItem>

				<ThemePicker />
			</StyledMenu>
			{aboutBox && (
				<AboutBox
					{...aboutBox}
					open={aboutBoxOpen}
					setOpen={setAboutBoxOpen}
				/>
			)}
		</InlineDiv>
	);
};

export default UserProfile;
